/**
 * Repository Features Page
 *
 * Displays extracted features for a repository with filtering and sorting
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, RefreshCw, Loader2 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@forgenetwork/ui';
import { FeatureList } from '@/components/features';
import { useFeatures, useExtractFeatures } from '@/hooks/useFeatures';
import { useToast } from '@/shared/hooks/useToast';

export const Route = createFileRoute('/_authenticated/repositories/$repoId/features')({
  component: RepositoryFeaturesPage,
});

function RepositoryFeaturesPage(): React.JSX.Element {
  const { repoId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch features
  const {
    data: featuresData,
    isLoading,
    error,
    refetch,
  } = useFeatures(repoId);

  // Extract features mutation
  const extractFeatures = useExtractFeatures();

  const handleExtract = (): void => {
    extractFeatures.mutate(
      { repositoryId: repoId, force: true },
      {
        onSuccess: () => {
          toast({
            title: 'Feature Extraction Started',
            description: 'Features are being extracted from the repository.',
          });
        },
        onError: (error: Error) => {
          toast({
            title: 'Extraction Failed',
            description: error.message || 'Failed to start feature extraction',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleRefresh = (): void => {
    refetch();
    toast({
      title: 'Refreshing Features',
      description: 'Fetching latest feature data...',
    });
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">
                {error instanceof Error ? error.message : 'Failed to load features'}
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: '..' })}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Repository
                </Button>
                <Button variant="default" onClick={handleExtract}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Extract Features
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: '..' })}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Repository Features</h1>
              <p className="text-muted-foreground mt-1">
                Extracted features and capabilities from the codebase
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="default"
              onClick={handleExtract}
              disabled={extractFeatures.isPending}
            >
              {extractFeatures.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Re-Extract Features
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Feature Statistics */}
        {featuresData && !isLoading && (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{featuresData.total}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  High Confidence
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">
                  {featuresData.features.filter((f) => f.confidence >= 0.8).length}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">
                  {new Set(featuresData.features.map((f) => f.category)).size}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Feature List */}
        <Card>
          <CardHeader>
            <CardTitle>Features</CardTitle>
            <CardDescription>
              Browse and filter detected features in this repository
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FeatureList
              features={featuresData?.features ?? []}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
